import { BudgetAmountStatus } from '@/components/cashflow/budget/BudgetAmountStatus';
import { BudgetBarChart } from '@/components/cashflow/budget/BudgetBarChart';
import { BudgetCategoryGroupsTable } from '@/components/cashflow/budget/BudgetCategoryGroupsTable';
import { useBudgetContext } from '@/components/cashflow/budget/BudgetContext';
import { BudgetControls } from '@/components/cashflow/budget/BudgetControls';
import { BudgetTransactionEntriesTable } from '@/components/cashflow/budget/BudgetTransactionEntriesTable';
import { TRANSACTION_ENTRIES_TABLE_Z_INDEX } from '@/components/cashflow/transactions/TransactionEntriesTable';
import { FlexCol, FlexRow } from '@/components/common/Flex';
import PlentySpinner from '@/components/common/PlentySpinner';
import { BigHeader, Text } from '@/components/common/Typography';
import { useQuery } from '@apollo/client';
import { Box, Center, Flex } from '@chakra-ui/react';
import {
    BudgetCategoryGroupFieldsFragment,
    BudgetDocument,
    BudgetFieldsFragment,
    BudgetPurpose,
    TransactionEntryCategoryGroupFieldsFragment,
} from '@common/graphql/generated';
import {
    filterTransactionEntriesForBudgetPurpose,
    getBudgetPurposeLabel,
    isSpendingBudgetPurpose,
    useActualAmountsByBudgetPurpose,
    useBudgetCategoryGroupsWithAverage,
    useBudgetedAmountForPurpose,
    useCategoryGroupsWithSpendingForBudgetPurpose,
} from '@common/react/budget';
import { ParentSize } from '@visx/responsive';
import { useRef } from 'react';

export const BudgetPurposeDetailDrawer = ({
    id: budgetPurpose,
}: {
    id: string;
}) => {
    const { budgetCategoryGroups, transactionEntryCategoryGroups } =
        useBudgetContext();

    const { data: budgetData, loading: budgetLoading } =
        useQuery(BudgetDocument);

    if (budgetLoading || !budgetData?.currentUserBudget) {
        return (
            <Center h="full">
                <PlentySpinner message={<Text>Loading...</Text>} />
            </Center>
        );
    }

    return (
        <BudgetPurposeDetailsDrawerInner
            budgetPurpose={budgetPurpose as BudgetPurpose}
            budget={budgetData.currentUserBudget}
            budgetCategoryGroups={budgetCategoryGroups}
            transactionEntryCategoryGroups={transactionEntryCategoryGroups}
        />
    );
};

const BudgetPurposeDetailsDrawerInner = ({
    budgetPurpose,
    budget,
    budgetCategoryGroups,
    transactionEntryCategoryGroups,
}: {
    budgetPurpose: BudgetPurpose;
    budget: BudgetFieldsFragment;
    budgetCategoryGroups: BudgetCategoryGroupFieldsFragment[];
    transactionEntryCategoryGroups: TransactionEntryCategoryGroupFieldsFragment[];
}) => {
    const scrollRef = useRef<HTMLDivElement>(null);
    const { dateRange, transactionEntries, loading } = useBudgetContext();
    const budgetedAmount = useBudgetedAmountForPurpose({
        budget,
        budgetPurpose,
        dateRange,
    });
    const actualAmounts = useActualAmountsByBudgetPurpose({
        budgetCategoryGroups,
        transactionEntries,
    });
    const actualAmount = actualAmounts[budgetPurpose];
    const amountLeft = budgetedAmount - actualAmount;
    const percentage = Math.abs(
        (actualAmount - budgetedAmount) / budgetedAmount
    );
    const transactionEntriesForBudgetPurpose =
        filterTransactionEntriesForBudgetPurpose({
            budgetCategoryGroups,
            transactionEntries,
            budgetPurpose,
        });

    const categoryGroupsWithSpending =
        useCategoryGroupsWithSpendingForBudgetPurpose({
            budgetCategoryGroups,
            transactionEntries,
            transactionEntryCategoryGroups,
            budgetPurpose,
            dateRange,
        });

    // Preload category groups with average spending so that opening the "Edit budget" modal is faster
    useBudgetCategoryGroupsWithAverage();

    const enabledCategoryGroups = categoryGroupsWithSpending.filter(
        (g) => g.enabled
    );
    const disabledCategoryGroups = categoryGroupsWithSpending.filter(
        (g) => !g.enabled
    );

    return (
        <Flex direction="column" ref={scrollRef} h="100%" overflowY="scroll">
            <FlexRow
                py={8}
                px={10}
                bg="gray.50"
                justify="space-between"
                align="center"
                position="sticky"
                top={0}
                zIndex={TRANSACTION_ENTRIES_TABLE_Z_INDEX + 1}
            >
                <BigHeader>{getBudgetPurposeLabel(budgetPurpose)}</BigHeader>
                <BudgetControls budgetPurpose={budgetPurpose} />
            </FlexRow>

            <FlexCol pt={10} px={10}>
                <BudgetAmountStatus
                    budgetedAmount={budgetedAmount}
                    budgetPurpose={budgetPurpose}
                    amountLeft={amountLeft}
                    percentage={percentage}
                    withPrefix={true}
                    loading={loading}
                />

                <Box mt={8}>
                    <ParentSize>
                        {({ width }) => (
                            <BudgetBarChart
                                width={width}
                                budgetedAmount={budgetedAmount}
                                actualAmount={actualAmount}
                                budgetPurpose={budgetPurpose}
                            />
                        )}
                    </ParentSize>
                </Box>

                {isSpendingBudgetPurpose(budgetPurpose) && (
                    <>
                        <BudgetCategoryGroupsTable
                            categoryGroups={enabledCategoryGroups}
                            isEnabled={true}
                            loading={loading}
                        />

                        <BudgetCategoryGroupsTable
                            categoryGroups={disabledCategoryGroups}
                            isEnabled={false}
                            loading={loading}
                        />
                    </>
                )}

                <BudgetTransactionEntriesTable
                    title={getBudgetPurposeLabel(budgetPurpose)}
                    transactionEntries={transactionEntriesForBudgetPurpose}
                    loading={loading}
                    scrollRef={scrollRef}
                />
            </FlexCol>
        </Flex>
    );
};
